import { useMemo } from 'react';
import { Globe } from 'lucide-react';
import { Select } from './Select';
import type { SelectProps } from './Select';
import { timezoneLabel } from '../../../shared/utils';

/** Sensible defaults when the runtime cannot enumerate the tz database. */
const FALLBACK_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Europe/Athens',
  'Europe/Istanbul',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Perth',
  'Australia/Sydney',
  'Pacific/Auckland',
  'UTC',
] as const;

function listTimezones(): string[] {
  const supported = (
    Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  if (typeof supported === 'function') {
    try {
      const zones = supported('timeZone');
      if (Array.isArray(zones) && zones.length) return zones;
    } catch {
      /* fall through to the curated list */
    }
  }
  return [...FALLBACK_ZONES];
}

/** The browser's own zone — the right default for a brand-new workspace. */
export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';
  } catch {
    return 'America/Chicago';
  }
}

export interface TimezoneSelectProps
  extends Omit<SelectProps, 'options' | 'children' | 'value' | 'onChange'> {
  value: string;
  onChange: (timezone: string) => void;
}

/**
 * Workspace timezone picker. Every schedule in the app is rendered in the
 * workspace zone, so this choice decides what "9am" means for the whole team.
 */
export function TimezoneSelect({ value, onChange, ...props }: TimezoneSelectProps) {
  const options = useMemo(() => {
    const zones = listTimezones();
    const withCurrent = zones.includes(value) ? zones : [value, ...zones];
    return withCurrent.map((zone) => ({
      value: zone,
      label: `${zone.replace(/_/g, ' ')} · ${timezoneLabel(zone)}`,
    }));
  }, [value]);

  return (
    <Select
      {...props}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      options={options}
      leadingIcon={<Globe />}
    />
  );
}
